import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { 
  FiDollarSign,
  FiCalendar,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiInfo,
  FiCreditCard,
  FiFileText,
  FiDownload
} from 'react-icons/fi';
import './TagihanMahasiswa.css';

const TagihanMahasiswa = () => {
  const { user } = useAuth();
  const [tagihanList, setTagihanList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, lunas, belum_lunas, terlambat
  const [stats, setStats] = useState({
    totalTagihan: 0,
    totalBelumLunas: 0,
    totalLunas: 0,
    totalTerlambat: 0
  });

  const fetchTagihan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/tagihan', {
        params: {
          nim: user.nim
        }
      });

      const data = res.data.data || [];
      setTagihanList(data);

      // Calculate stats
      const belumLunas = data.filter(t => t.status === 'belum_lunas');
      const lunas = data.filter(t => t.status === 'lunas');
      const terlambat = data.filter(t => {
        if (t.status !== 'belum_lunas') return false;
        const deadline = new Date(t.deadline);
        return deadline < new Date();
      });

      setStats({
        totalTagihan: data.reduce((sum, t) => sum + (t.jumlah || 0), 0),
        totalBelumLunas: belumLunas.reduce((sum, t) => sum + (t.jumlah || 0), 0),
        totalLunas: lunas.reduce((sum, t) => sum + (t.jumlah || 0), 0),
        totalTerlambat: terlambat.length
      });

    } catch (error) {
      console.error('Error fetching tagihan:', error);
      if (error.response?.status !== 404) {
        toast.error('Gagal memuat data tagihan');
      }
      setTagihanList([]);
    } finally {
      setLoading(false);
    }
  }, [user.nim]);

  useEffect(() => {
    fetchTagihan();
  }, [fetchTagihan]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (tagihan) => {
    if (tagihan.status === 'lunas') {
      return {
        text: 'Lunas',
        color: '#10b981',
        bgColor: '#d1fae5',
        icon: FiCheckCircle
      };
    }

    const deadline = new Date(tagihan.deadline);
    const today = new Date();
    
    if (deadline < today) {
      return {
        text: 'Terlambat',
        color: '#ef4444',
        bgColor: '#fee2e2',
        icon: FiAlertCircle
      };
    }

    return {
      text: 'Belum Lunas',
      color: '#f59e0b',
      bgColor: '#fef3c7',
      icon: FiClock
    };
  };

  const filteredTagihan = tagihanList.filter(tagihan => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'lunas') return tagihan.status === 'lunas';
    if (filterStatus === 'belum_lunas') {
      const deadline = new Date(tagihan.deadline);
      return tagihan.status === 'belum_lunas' && deadline >= new Date();
    }
    if (filterStatus === 'terlambat') {
      const deadline = new Date(tagihan.deadline);
      return tagihan.status === 'belum_lunas' && deadline < new Date();
    }
    return true;
  });

  const handleBayar = (tagihan) => {
    // Redirect to payment page or open payment modal
    toast.info('Fitur pembayaran akan segera tersedia');
  };

  return (
    <div className="tagihan-mahasiswa-container">
      {/* Header Section */}
      <div className="tagihan-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="icon-wrapper">
              <FiDollarSign className="header-icon" />
            </div>
            <div>
              <h1 className="header-title">Tagihan Pembayaran</h1>
              <p className="header-subtitle">Kelola dan pantau tagihan kuliah Anda</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Tagihan</p>
            <h2 className="stat-value">{formatCurrency(stats.totalTagihan)}</h2>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <FiClock />
          </div>
          <div className="stat-details">
            <p className="stat-label">Belum Lunas</p>
            <h2 className="stat-value">{formatCurrency(stats.totalBelumLunas)}</h2>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-details">
            <p className="stat-label">Sudah Lunas</p>
            <h2 className="stat-value">{formatCurrency(stats.totalLunas)}</h2>
          </div>
        </div>

        <div className="stat-card red">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-details">
            <p className="stat-label">Tagihan Terlambat</p>
            <h2 className="stat-value">{stats.totalTerlambat}</h2>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-card">
        <div className="filter-header">
          <FiFileText />
          <h3>Filter Tagihan</h3>
        </div>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Semua
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'belum_lunas' ? 'active' : ''}`}
            onClick={() => setFilterStatus('belum_lunas')}
          >
            Belum Lunas
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'lunas' ? 'active' : ''}`}
            onClick={() => setFilterStatus('lunas')}
          >
            Lunas
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'terlambat' ? 'active' : ''}`}
            onClick={() => setFilterStatus('terlambat')}
          >
            Terlambat
          </button>
        </div>
      </div>

      {/* Tagihan List */}
      <div className="tagihan-list-card">
        <div className="card-header">
          <h3>
            <FiFileText />
            Daftar Tagihan
          </h3>
          <span className="table-count">{filteredTagihan.length} tagihan</span>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Memuat data tagihan...</p>
          </div>
        ) : filteredTagihan.length === 0 ? (
          <div className="empty-state">
            <FiInfo size={48} />
            <p>Tidak ada tagihan ditemukan</p>
            <small>Anda tidak memiliki tagihan untuk filter ini</small>
          </div>
        ) : (
          <div className="tagihan-grid">
            {filteredTagihan.map((tagihan) => {
              const statusBadge = getStatusBadge(tagihan);
              const StatusIcon = statusBadge.icon;
              const isOverdue = tagihan.status === 'belum_lunas' && new Date(tagihan.deadline) < new Date();

              return (
                <div key={tagihan._id} className={`tagihan-card ${isOverdue ? 'overdue' : ''}`}>
                  <div className="tagihan-card-header">
                    <div className="tagihan-type">
                      <div className="type-icon">
                        <FiFileText />
                      </div>
                      <div>
                        <h4 className="type-title">{tagihan.jenis_tagihan || 'Tagihan'}</h4>
                        <p className="type-subtitle">{tagihan.semester} - {tagihan.tahun_ajaran}</p>
                      </div>
                    </div>
                    <div 
                      className="status-badge-new"
                      style={{ 
                        background: statusBadge.bgColor,
                        color: statusBadge.color 
                      }}
                    >
                      <StatusIcon size={14} />
                      <span>{statusBadge.text}</span>
                    </div>
                  </div>

                  <div className="tagihan-card-body">
                    <div className="amount-section">
                      <div className="amount-label">Jumlah Tagihan</div>
                      <div className="amount-value">{formatCurrency(tagihan.jumlah)}</div>
                    </div>

                    <div className="info-grid">
                      <div className="info-item">
                        <FiCalendar className="info-icon" />
                        <div>
                          <div className="info-label">Tanggal Tagihan</div>
                          <div className="info-value">{formatDate(tagihan.created_at || tagihan.deadline)}</div>
                        </div>
                      </div>

                      <div className="info-item">
                        <FiAlertCircle className="info-icon" style={{ color: isOverdue ? '#ef4444' : '#667eea' }} />
                        <div>
                          <div className="info-label">Jatuh Tempo</div>
                          <div className="info-value" style={{ color: isOverdue ? '#ef4444' : 'inherit' }}>
                            {formatDate(tagihan.deadline)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {tagihan.keterangan && (
                      <div className="keterangan-section">
                        <FiInfo className="info-icon" />
                        <div className="keterangan-text">{tagihan.keterangan}</div>
                      </div>
                    )}
                  </div>

                  {tagihan.status !== 'lunas' && (
                    <div className="tagihan-card-footer">
                      <button 
                        className="btn-primary-action"
                        onClick={() => handleBayar(tagihan)}
                      >
                        <FiCreditCard />
                        <span>Bayar Sekarang</span>
                      </button>
                      <button 
                        className="btn-secondary-action"
                        onClick={() => toast.info('Fitur unduh invoice akan segera tersedia')}
                      >
                        <FiDownload />
                      </button>
                    </div>
                  )}

                  {tagihan.status === 'lunas' && (
                    <div className="tagihan-card-footer paid">
                      <div className="paid-badge">
                        <FiCheckCircle />
                        <span>Pembayaran Telah Lunas</span>
                      </div>
                      <button 
                        className="btn-secondary-action"
                        onClick={() => toast.info('Fitur unduh bukti akan segera tersedia')}
                      >
                        <FiDownload />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="info-box">
        <FiInfo />
        <div>
          <strong>Informasi Penting:</strong>
          <ul>
            <li>Harap melakukan pembayaran sebelum tanggal jatuh tempo untuk menghindari denda</li>
            <li>Simpan bukti pembayaran untuk keperluan administrasi</li>
            <li>Hubungi bagian keuangan jika ada pertanyaan terkait tagihan</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TagihanMahasiswa;
